import { max, scaleLinear, ZoomTransform } from 'd3'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Bound } from 'state/mint/v3/actions'

import { Area } from './Area'
import { AxisBottom } from './AxisBottom'
import { Brush } from './Brush'
import { Line } from './Line'
import { ChartEntry, LiquidityChartRangeInputProps } from './types'
import Zoom, { ZoomOverlay } from './Zoom'

export const xAccessor = (d: ChartEntry) => d.price0
export const yAccessor = (d: ChartEntry) => d.activeLiquidity

export function Chart(props: LiquidityChartRangeInputProps) {
  const {
    id = 'liquidityChartRangeInput',
    data: { series, current },
    ticksAtLimit,
    styles,
    dimensions: { width, height },
    margins,
    interactive = true,
    brushDomain,
    brushLabels,
    onBrushDomainChange,
    zoomLevels,
  } = props

  const zoomRef = useRef<SVGRectElement | null>(null)
  const [zoom, setZoom] = useState<ZoomTransform | null>(null)

  const isFullRange = Boolean(ticksAtLimit?.[Bound.LOWER] && ticksAtLimit?.[Bound.UPPER])
  const normalize = (d: [number, number]) => (d[0] <= d[1] ? d : [d[1], d[0]]) as [number, number]

  const [innerHeight, innerWidth] = useMemo(
    () => [height - margins.top - margins.bottom, width - margins.left - margins.right],
    [width, height, margins]
  )

  const percentile = (sorted: number[], p: number) =>
    sorted[Math.max(0, Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1))))]

  const baseViewDomain = useMemo<[number, number]>(() => {
    const finiteWindow: [number, number] = [
      Math.max(0, current * zoomLevels.initialMin),
      current * zoomLevels.initialMax,
    ]

    if (!isFullRange) return finiteWindow

    const xs = series.map(xAccessor).filter((v) => Number.isFinite(v) && v >= 0)
    if (xs.length < 2) return finiteWindow

    xs.sort((a, b) => a - b)
    const q1 = percentile(xs, 0.01)
    const q99 = percentile(xs, 0.99)

    let d0 = Math.max(0, q1)
    let d1 = q99

    const SPREAD_CAP = 1e6
    if (!(d1 > d0) || d1 / Math.max(d0, 1e-12) > SPREAD_CAP || !Number.isFinite(d1)) {
      return finiteWindow
    }

    // pad the quantile window
    const PAD = Math.max((d1 - d0) * 0.02, 1e-12)
    d0 = Math.max(0, d0 - PAD)
    d1 = d1 + PAD

    // 🔧 ensure the current price is visible
    const CUR_PAD = 0.08 // small visual margin around current
    if (current < d0) d0 = Math.max(0, current * (1 - CUR_PAD))
    if (current > d1) d1 = current * (1 + CUR_PAD)

    return [d0, d1]
  }, [isFullRange, series, current, zoomLevels.initialMin, zoomLevels.initialMax])

  const { xScale, yScale } = useMemo(() => {
    const base = {
      xScale: scaleLinear().domain(baseViewDomain).range([4, innerWidth]),
      yScale: scaleLinear()
        .domain([0, max(series, yAccessor) as number])
        .range([innerHeight, 0]),
    }

    if (zoom) {
      const z = zoom.rescaleX(base.xScale)
      base.xScale.domain(z.domain() as [number, number])
    }

    return base
  }, [baseViewDomain, innerWidth, innerHeight, series, zoom])

  useEffect(() => setZoom(null), [zoomLevels])

  useEffect(() => {
    if (!brushDomain) onBrushDomainChange(xScale.domain() as [number, number], undefined)
  }, [brushDomain, onBrushDomainChange, xScale])

  return (
    <>
      <Zoom
        svg={zoomRef.current}
        xScale={xScale}
        setZoom={setZoom}
        width={innerWidth}
        height={height}
        resetBrush={() => {
          onBrushDomainChange(
            [current * zoomLevels.initialMin, current * zoomLevels.initialMax] as [number, number],
            'reset'
          )
        }}
        showResetButton={true}
        // showResetButton={Boolean(ticksAtLimit[Bound.LOWER] || ticksAtLimit[Bound.UPPER])}
        zoomLevels={zoomLevels}
      />

      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        {/* <defs>
          <clipPath id={`${id}-chart-clip`}>
            <rect x="0" y="0" width={innerWidth} height={height} />
          </clipPath>
          {renderSelectionMask}
        </defs> */}
        <defs>
          <clipPath id={`${id}-chart-clip`}>
            <rect x="0" y="0" width={innerWidth} height={height} />
          </clipPath>

          {brushDomain && (
            // mask to highlight selected area (tints full width when full range)
            <mask id={`${id}-chart-area-mask`}>
              <rect
                fill="white"
                x={xScale(brushDomain[0])}
                y="0"
                width={Math.max(1, xScale(brushDomain[1]) - xScale(brushDomain[0]))}
                height={innerHeight}
              />
            </mask>
          )}
        </defs>

        <g transform={`translate(${margins.left},${margins.top})`}>
          <g clipPath={`url(#${id}-chart-clip)`}>
            {/* <rect
              x={fullRectX}
              y={0}
              width={fullRectW}
              height={innerHeight}
              fill={styles.area.selection}
              opacity={0.35}
              {...(!isFullRange ? { mask: `url(#${id}-selection-mask)` } : {})}
            /> */}

            <Area
              series={series}
              xScale={xScale}
              yScale={yScale}
              xValue={xAccessor}
              yValue={yAccessor}
              fill={styles.area.selection}
            />

            <Line value={current} xScale={xScale} innerHeight={innerHeight} />
            <AxisBottom xScale={xScale} innerHeight={innerHeight} />
          </g>

          <ZoomOverlay width={innerWidth} height={height} ref={zoomRef} />

          <Brush
            id={id}
            xScale={xScale}
            interactive={interactive}
            brushLabelValue={brushLabels}
            brushExtent={brushDomain ?? (xScale.domain() as [number, number])}
            innerWidth={innerWidth}
            innerHeight={innerHeight}
            setBrushExtent={onBrushDomainChange}
            westHandleColor={styles.brush.handle.west}
            eastHandleColor={styles.brush.handle.east}
            showWestHandle={true}
            showEastHandle={!isFullRange}
          />
        </g>
      </svg>
    </>
  )
}
